import type { HotelListOptions } from '@bandido/shared/types/hotels/hotel-search-results';
import type { HotelCancellationView } from '@bandido/shared/types/hotels/hotel-cancellation';
import type { HotelResultPolicy, HotelSearchExecution } from '@bandido/shared/types/hotels/hotel-search-progress';
import {
  HOTEL_BOARD_CATEGORIES,
  HOTEL_REFUNDABILITY,
  HotelSearchFilters,
  HotelNormalizedBoard,
  HotelRefundability,
  HotelBoardCategory,
} from '@bandido/shared/types';
import type { PlaceKind } from '@bandido/shared/types';

export type HotelLocation =
  | { type: 'destination'; placeId: string; label: string }
  | { type: 'point'; latitude: number; longitude: number; label: string };
export interface HotelSearchDraft {
  filters?: HotelSearchFilters;
  results?: HotelListOptions;
  location: HotelLocation | null;
  checkIn: string;
  checkOut: string;
  rooms: { paxes: number[] }[];
  radiusKm: number;
  nationality: string;
}
export interface HotelSurcharge {
  charge_type?: string | null;
  description: string | null;
  included: boolean | null;
  price: { amount: string; currency: string } | null;
}
export interface HotelFare {
  cancellation?: HotelCancellationView;
  refundability?: HotelRefundability;
  board_categories?: HotelBoardCategory[];
  board_mixed?: boolean;
  fare_id: string;
  property_id: string;
  search_id: string;
  connector_account_id: string;
  total_price: { amount: string; currency: string };
  refundable: boolean | null;
  cancellation_summary: string | null;
  room_stays: {
    room_index: number;
    adults: number;
    children_ages: number[];
    room_name: string | null;
    rate_name: string | null;
    board_basis: string | null;
    board?: HotelNormalizedBoard;
  }[];
  connector_lineage: {
    access_code?: string | null;
    supplier_code?: string | null;
    payment_type?: string | null;
    option_status?: string | null;
    rate_rules: string[];
    surcharges: HotelSurcharge[];
    rooms: {
      room_index: number;
      occupancy_ref_id?: string | null;
      room_code?: string | null;
      room_supplier_code?: string | null;
      room_description?: string | null;
      supplier_room_description?: string | null;
      surcharges: HotelSurcharge[];
    }[];
  };
}
export interface HotelSummary {
  hotel_id: string;
  display_name: string;
  distance_km?: number | null;
}
export interface HotelSearchState {
  search_id: string;
  status: 'accepted' | 'running' | 'completed' | 'failed';
  partial: boolean;
  warnings: string[];
  progress: Record<string, number>;
  executions?: HotelSearchExecution[];
  result_policy?: HotelResultPolicy;
}
export const HOTEL_POLL_TIMEOUT_MS = 120000;
export function isTouristDestination(kind: PlaceKind): boolean {
  return ['country', 'region', 'district', 'city', 'neighborhood', 'beach'].includes(kind);
}
export function validPoint(lat: unknown, lng: unknown): boolean {
  return (
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    lat >= -90 &&
    lat <= 90 &&
    typeof lng === 'number' &&
    Number.isFinite(lng) &&
    lng >= -180 &&
    lng <= 180
  );
}
export function hotelOccupancies(rooms: { paxes: number[] }[]) {
  if (rooms.length < 1 || rooms.length > 30) throw new Error('rooms');
  return rooms.map(({ paxes }) => {
    if (!paxes.length || paxes.some((age) => !Number.isInteger(age) || age < 0 || age > 120)) throw new Error('ages');
    const adults = paxes.filter((age) => age >= 18).length;
    const children_ages = paxes.filter((age) => age < 18);
    if (!adults || adults > 30 || children_ages.length > 30) throw new Error('adults');
    return { adults, children_ages };
  });
}
function validDate(value: string) {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value
  );
}
export function normalizedHotelFilters(value?: HotelSearchFilters): HotelSearchFilters {
  const categories = value?.board_categories ?? [];
  if (!Array.isArray(categories) || categories.some((c) => !HOTEL_BOARD_CATEGORIES.includes(c)))
    throw new Error('board filters');
  if (value?.refundability != null && !HOTEL_REFUNDABILITY.includes(value.refundability))
    throw new Error('refundability');
  const unique = [...new Set(categories)].sort();
  return {
    board_categories: unique.length === HOTEL_BOARD_CATEGORIES.length ? [] : unique,
    refundability: value?.refundability ?? null,
    include_package_rates: value?.include_package_rates ?? true,
  };
}
export function hotelSearchCriteria(draft: HotelSearchDraft, ids: string[]) {
  if (!validDate(draft.checkIn) || !validDate(draft.checkOut) || draft.checkOut <= draft.checkIn)
    throw new Error('dates');
  if (!/^[A-Z]{2}$/.test(draft.nationality)) throw new Error('nationality');
  if (!ids.length || ids.length > 200 || new Set(ids).size !== ids.length) throw new Error('hotels');
  return {
    search_mode: 'property_ids',
    result_mode: 'summary' as const,
    filters: normalizedHotelFilters(draft.filters),
    check_in: draft.checkIn,
    check_out: draft.checkOut,
    occupancies: hotelOccupancies(draft.rooms),
    currency: 'USD',
    markets: [draft.nationality],
    language: 'es',
    nationality: draft.nationality,
    adult_pax_age: 30,
    property_ids: ids,
  };
}
// Compare decimal strings without rounding or converting money to JS floats.
export function compareHotelAmounts(left: string, right: string): number {
  const parts = (value: string) => {
    if (!/^\d+(\.\d+)?$/.test(value)) throw new Error('amount');
    const [whole, fraction = ''] = value.split('.');
    return [whole.replace(/^0+(?=\d)/, ''), fraction];
  };
  const [a, af] = parts(left),
    [b, bf] = parts(right);
  if (a.length !== b.length) return a.length - b.length;
  if (a !== b) return a < b ? -1 : 1;
  const width = Math.max(af.length, bf.length);
  return af.padEnd(width, '0') < bf.padEnd(width, '0') ? -1 : af.padEnd(width, '0') > bf.padEnd(width, '0') ? 1 : 0;
}
/** Display only: round decimal strings to two places without floating-point conversion. */
export function formatHotelAmount(amount: string): string {
  if (!/^\d+(\.\d+)?$/.test(amount)) throw new Error('amount');
  const [whole, fraction = ''] = amount.split('.');
  const cents = BigInt(whole + fraction.padEnd(2, '0').slice(0, 2)) + (fraction[2] >= '5' ? 1n : 0n);
  const digits = cents.toString().padStart(3, '0');
  return `${digits.slice(0, -2)}.${digits.slice(-2)}`;
}
export function hotelFareIsPackage(fare: HotelFare): boolean {
  return fare.connector_lineage.rate_rules.some((rule) => rule.trim().toUpperCase() === 'PACKAGE');
}
export function groupHotelFares(fares: HotelFare[], showPackages: boolean) {
  const grouped = new Map<string, HotelFare[]>();
  for (const fare of fares) {
    if (!showPackages && hotelFareIsPackage(fare)) continue;
    const group = grouped.get(fare.property_id) ?? [];
    // Poll snapshots replace state; duplicate IDs never become duplicate cards.
    if (!group.some((item) => item.fare_id === fare.fare_id && item.search_id === fare.search_id)) group.push(fare);
    grouped.set(fare.property_id, group);
  }
  return [...grouped.entries()].map(([hotelId, offers]) => ({
    hotelId,
    offers: [...offers].sort(
      (a, b) =>
        a.total_price.currency.localeCompare(b.total_price.currency) ||
        compareHotelAmounts(a.total_price.amount, b.total_price.amount),
    ),
  }));
}
export function shouldPollHotels(status: HotelSearchState['status'], elapsedMs: number) {
  return (status === 'accepted' || status === 'running') && elapsedMs < HOTEL_POLL_TIMEOUT_MS;
}

export function stableResultRows<T>(previous: T[], next: T[], identity: (item: T) => string): T[] {
  const remaining = new Map(next.map((item) => [identity(item), item]));
  const kept = previous.flatMap((item) => {
    const id = identity(item),
      value = remaining.get(id);
    remaining.delete(id);
    return value === undefined ? [] : [value];
  });
  return [...kept, ...remaining.values()];
}

/** Acquisition is immutable. Broadening this scope requires an explicit new Search. */
export function hotelPackagesNeedNewSearch(acquisition: boolean | undefined, filters: HotelSearchFilters): boolean {
  return acquisition === false && filters.include_package_rates !== false;
}
